import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StartGameOverlay from './StartGameOverlay';
import RetroLoadingDots from './RetroLoadingDots';

const RetroColorBubbles = ({ 
  isActive = false, 
  onEnd,
  onGameComplete,
  showStartButton = false,
  isGenerating = false 
}) => {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  // Show start button only when explicitly requested (like in RetroArcade)
  const [waitingToStart, setWaitingToStart] = useState(showStartButton);
  const containerRef = useRef(null);
  const comboTimeoutRef = useRef(null);

  const colors = [
    '#FF00FF', // Hot pink
    '#FF6B00', // Orange
    '#00FF9F', // Neon green
    '#FFB800', // Amber
  ];

  // Initialize game
  useEffect(() => {
    if (isActive && !gameStarted && !waitingToStart) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      
      // Create initial bubble pattern
      const newBubbles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * (width - 60),
        y: Math.random() * (height - 60),
        size: 40,
        color: colors[Math.floor(Math.random() * colors.length)],
        scale: 1,
        popped: false
      }));
      
      setBubbles(newBubbles);
      setScore(0);
      setCombo(0);
      setGameStarted(true);
      setTimeLeft(10);
    }
  }, [isActive, waitingToStart]);

  const handleStart = () => {
    setWaitingToStart(false);
  };

  // Game timer - only runs when game has actually started
  useEffect(() => {
    if (!gameStarted || waitingToStart) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onGameComplete?.({
            type: 'colorbubbles',
            score,
            title: 'Color Bubbles Results',
            message: `You popped ${score} bubbles!`,
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, waitingToStart, score]);

  const handleBubblePop = (bubble, index) => {
    if (!gameStarted) return;

    // Clear previous combo timeout
    if (comboTimeoutRef.current) {
      clearTimeout(comboTimeoutRef.current);
    }

    // Update combo
    setCombo(prev => prev + 1);

    // Start combo timeout
    comboTimeoutRef.current = setTimeout(() => {
      setCombo(0);
    }, 500);

    // Update score based on combo
    setScore(prev => prev + Math.pow(2, Math.min(combo, 4)));

    // Mark bubble as popped
    setBubbles(prev => prev.map((b, i) => 
      i === index ? { ...b, popped: true } : b
    ));

    // Generate new bubble
    setTimeout(() => {
      if (!containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      setBubbles(prev => prev.map((b, i) => 
        i === index ? {
          ...b,
          x: Math.random() * (width - 60),
          y: Math.random() * (height - 60),
          color: colors[Math.floor(Math.random() * colors.length)],
          popped: false
        } : b
      ));
    }, 300);
  };

  return (
    <motion.div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <AnimatePresence>
        {waitingToStart ? (
          <StartGameOverlay 
            onStart={handleStart}
            gameName="Color Bubbles"
          />
        ) : (
          <>
            {/* Score and Timer */}
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
              <div className="text-white font-['VT323'] text-3xl">
                {score}
              </div>
              <div className="text-white font-['VT323'] text-3xl">
                {timeLeft}s
              </div>
            </div>

            {/* Combo Display */}
            <AnimatePresence>
              {combo > 1 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.5, y: -20 }}
                  className="absolute top-16 left-4 text-white font-['VT323'] text-2xl"
                >
                  {combo}x COMBO!
                </motion.div>
              )}
            </AnimatePresence>

            {/* Bubbles */}
            <AnimatePresence>
              {bubbles.map((bubble, index) => (
                <motion.div
                  key={bubble.id}
                  className="absolute cursor-pointer"
                  style={{
                    width: bubble.size,
                    height: bubble.size,
                    x: bubble.x,
                    y: bubble.y,
                  }}
                  initial={{ scale: 0 }}
                  animate={{
                    scale: bubble.popped ? [1, 1.5, 0] : 1,
                    opacity: bubble.popped ? [1, 0] : 1
                  }}
                  exit={{ scale: 0 }}
                  onClick={() => !bubble.popped && handleBubblePop(bubble, index)}
                  whileHover={{ scale: 1.1 }}
                >
                  <div
                    className="w-full h-full rounded-full"
                    style={{
                      backgroundColor: bubble.color,
                      boxShadow: `0 0 20px ${bubble.color}`,
                    }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </>
        )}
      </AnimatePresence>


    </motion.div>
  );
};

export default RetroColorBubbles; 